<script setup lang="ts">
import { computed } from 'vue';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import { CriteriaThresholdEnum } from '@/core/conversations/application-score/application-score.type';

import StarIcon from '@/assets/svg/conversations/StarIcon.vue';
import StarHalfIcon from '@/assets/svg/conversations/StarHalfIcon.vue';

const props = withDefaults(
  defineProps<{
    score?: number;
    width?: number;
    height?: number;
    isViewed?: boolean;
  }>(),
  {
    score: 0,
    width: 16,
    height: 16,
  },
);

function normalizeScoreValue(score: number): number {
  return Number(ApplicationScoreService.formatScore(score));
}

const POSSIBLE_TOTAL_STARS = 5;
const applicationScore = computed(() => normalizeScoreValue(props?.score || 0));
const numFullStars = computed(() => Math.floor(applicationScore.value));
const hasHalfStar = computed(() => applicationScore.value - numFullStars.value >= 0.5);
const numEmptyStars = computed(
  () => POSSIBLE_TOTAL_STARS - numFullStars.value - Number(hasHalfStar.value),
);
const hasScore = computed(() => typeof props.score === 'number');
const isViewed = computed(() => props.isViewed ?? false);

const starsColors = computed(() => {
  // GREEN ====
  if (applicationScore.value >= CriteriaThresholdEnum.GREEN) {
    if (isViewed.value) {
      return {
        backgroundColor: '#BEF4D5',
        halfStarLeftColor: '#2BBA69',
        halfStarRightColor: '#FFFFFF',
        halfStarRightBackgroundColor: '#DEFAEA',
      };
    } else {
      return {
        backgroundColor: '#53D58B',
        halfStarLeftColor: '#184F32',
        halfStarRightColor: '#DEFAEA',
        halfStarRightBackgroundColor: '#BEF4D5',
      };
    }
  }

  // YELLOW ====
  if (applicationScore.value >= CriteriaThresholdEnum.YELLOW) {
    if (isViewed.value) {
      return {
        backgroundColor: '#FBEABC',
        halfStarLeftColor: '#E1BB56',
        halfStarRightColor: '#FFFFFF',
        halfStarRightBackgroundColor: '#FFF4D8',
      };
    } else {
      return {
        backgroundColor: '#EDCD77',
        halfStarLeftColor: '#5F4604',
        halfStarRightColor: '#FFF4D8',
        halfStarRightBackgroundColor: '#FBEABC',
      };
    }
  }

  // RED ====
  if (isViewed.value) {
    return {
      backgroundColor: '#F6CDCD',
      halfStarLeftColor: '#E96363',
      halfStarRightColor: '#FFFFFF',
      halfStarRightBackgroundColor: '#FDE6E6',
    };
  }
  return {
    backgroundColor: '#E96363',
    halfStarLeftColor: '#5A0303',
    halfStarRightColor: '#FDE6E6',
    halfStarRightBackgroundColor: '#F6CDCD',
  };
});
</script>

<template>
  <div v-if="hasScore" class="flex space-x-0.5">
    <StarIcon
      v-for="n in numFullStars"
      :key="n"
      alt="Star"
      :class="['rounded', 'self-center']"
      :bg="starsColors.backgroundColor"
      :star="starsColors.halfStarLeftColor"
      :width="width"
      :height="height"
    />
    <StarHalfIcon
      v-if="hasHalfStar"
      alt="Half-star"
      class="self-center"
      :bg-left="starsColors.backgroundColor"
      :bg-right="starsColors.halfStarRightBackgroundColor"
      :half-star-left="starsColors.halfStarLeftColor"
      :half-star-right="starsColors.halfStarRightColor"
      :width="width"
      :height="height"
    />
    <!-- Empty stars -->
    <template v-if="!isViewed">
      <img
        v-for="n in numEmptyStars"
        :key="n"
        src="@/assets/svg/conversations/star_empty.svg"
        alt="Star"
        :width="width"
        :height="height"
      />
    </template>
    <!-- Viewed Empty stars-->
    <template v-if="isViewed">
      <img
        v-for="n in numEmptyStars"
        :key="n"
        src="@/assets/svg/conversations/viewed_empty_star.svg"
        alt="Star"
        :width="width"
        :height="height"
      />
    </template>
  </div>
</template>
